import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Loading } from '@/components/ui';
import { ConversationCard } from '@/components/messages/ConversationCard';
import { ChatBottomSheet } from '@/components/messages/ChatBottomSheet';
import { ProfileBottomSheet } from '@/components/profile/ProfileBottomSheet';
import { useMessageStore } from '@/stores/messageStore';

export function MessagesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const conversations = useMessageStore((state) => state.conversations);
  const isLoading = useMessageStore((state) => state.isLoading);
  const error = useMessageStore((state) => state.error);

  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [selectedProfileUserId, setSelectedProfileUserId] = useState<string | null>(null);
  const [returnConversationId, setReturnConversationId] = useState<string | null>(null);

  useEffect(() => {
    const routeState = location.state as { openConversationId?: unknown } | null;
    const openConversationIdFromState =
      typeof routeState?.openConversationId === 'string'
        ? routeState.openConversationId
        : '';
    const searchParams = new URLSearchParams(location.search);
    const openConversationIdFromQuery = searchParams.get('openConversationId') ?? '';
    const openConversationId = openConversationIdFromState || openConversationIdFromQuery;

    if (openConversationId.length === 0) {
      return;
    }

    setSelectedConversationId(openConversationId);

    searchParams.delete('openConversationId');
    const search = searchParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      },
      { replace: true, state: null }
    );
  }, [location.pathname, location.search, location.state, navigate]);

  const handleConversationClick = useCallback((conversationId: string) => {
    setReturnConversationId(null);
    setSelectedConversationId(conversationId);
  }, []);

  const handleCloseChat = useCallback(() => {
    setSelectedConversationId(null);
  }, []);

  const handleOpenProfile = useCallback((userId: string) => {
    setReturnConversationId(null);
    setSelectedProfileUserId(userId);
  }, []);

  const handleOpenProfileFromChat = useCallback((userId: string) => {
    if (selectedConversationId) {
      setReturnConversationId(selectedConversationId);
      setSelectedConversationId(null);
    }
    setSelectedProfileUserId(userId);
  }, [selectedConversationId]);

  const handleCloseProfile = useCallback(() => {
    setSelectedProfileUserId(null);
    if (returnConversationId) {
      setSelectedConversationId(returnConversationId);
      setReturnConversationId(null);
    }
  }, [returnConversationId]);

  const handleOpenConversationFromProfile = useCallback((conversationId: string) => {
    setSelectedProfileUserId(null);
    setReturnConversationId(null);
    setSelectedConversationId(conversationId);
  }, []);

  const renderContent = () => {
    if (isLoading && conversations.length === 0) {
      return (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      );
    }

    if (error) {
      return (
        <div className="mx-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700 md:mx-0">
          {error}
        </div>
      );
    }

    if (conversations.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center px-8 pb-20 pt-16 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F0EDE8]">
            <MessageCircle className="h-6 w-6 text-[#C8C4BC]" />
          </div>
          <p className="text-[15px] font-medium text-[#AAA]">まだメッセージがありません</p>
          <p className="mt-1 text-[13px] text-[#C0BDB8]">ドナーにメッセージを送りましょう</p>
        </div>
      );
    }

    return (
      <div
        className="mx-4 overflow-hidden rounded-2xl bg-white md:mx-0"
        style={{ boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)' }}
      >
        <div>
          {conversations.map((conversation) => (
            <div key={conversation.id}>
              <ConversationCard
                conversation={conversation}
                onClick={handleConversationClick}
                onOpenProfile={handleOpenProfile}
              />
              {conversation.id !== conversations[conversations.length - 1]?.id && (
                <div className="ml-[72px] h-px bg-[#F0EDE8]" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-full bg-[#F7F4EF] pb-8">
        <div className="md:mx-auto md:max-w-4xl md:px-6 md:py-6">
          <div className="px-5 pb-5 pt-14 md:px-0 md:pt-0">
            <h1 className="text-[22px] font-semibold text-[#1A1A1A]">メッセージ</h1>
          </div>
          {renderContent()}
        </div>
      </div>

      <ChatBottomSheet
        conversationId={selectedConversationId}
        isOpen={!!selectedConversationId}
        onClose={handleCloseChat}
        onOpenProfile={handleOpenProfileFromChat}
      />

      <ProfileBottomSheet
        userId={selectedProfileUserId}
        isOpen={selectedProfileUserId !== null}
        onClose={handleCloseProfile}
        onOpenConversation={handleOpenConversationFromProfile}
      />
    </>
  );
}
